/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect } from 'react';
import { useRouteError } from 'react-router';
import { useTranslation } from 'react-i18next';

import { buttonVariants } from '@/design-system';
import { InlineLoading } from '@/components/loading/LoadingDots';
import { useDismissBootSplash } from '@/components/loading/bootSplash';
import { isChunkLoadError, reloadForStaleChunk } from './staleChunk';

// GMDS secondary (outline) button; recovery is offered, not pushed.
const RELOAD_BTN = buttonVariants({ variant: 'outline', className: 'coarse:min-h-10' });

/**
 * Router-level error boundary (the missing safety net behind the "Unexpected Application Error"
 * screen after a deploy). A redeploy swaps the hashed asset filenames out from under an open tab,
 * so a lazy route's JS or CSS chunk can 404 mid-navigation. When that error slips past the lazy
 * loader's own retry (e.g. the reload was gated against a loop, or a stale service worker re-served
 * the old shell), React Router would otherwise show its raw default error screen with no way out.
 *
 * Instead we detect the stale-chunk case and reload once to the fresh build, showing a quiet
 * "loading" state plus a manual refresh button for the rare gated case. Genuine errors still
 * surface with a readable message and a reload button.
 */
export function RouteErrorBoundary() {
  const { t } = useTranslation();
  const error = useRouteError();
  const stale = isChunkLoadError(error);
  // Both branches offer a button, so the boot splash must not be sitting on top of them.
  useDismissBootSplash();

  useEffect(() => {
    // Only auto-recover stale chunks; a genuine crash must not silently reload the tab.
    if (stale) reloadForStaleChunk();
  }, [stale]);

  if (stale) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <InlineLoading label={t('common.loading')} className="text-sm" />
        <button className={RELOAD_BTN} onClick={() => window.location.reload()}>
          {t('error.reload')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-xl font-medium text-[var(--color-foreground)]">{t('error.title')}</h1>
      <p className="max-w-md text-sm text-[var(--color-muted-foreground)]">
        {error instanceof Error ? error.message : String(error)}
      </p>
      <button className={RELOAD_BTN} onClick={() => window.location.reload()}>
        {t('error.reload')}
      </button>
    </div>
  );
}
